// Formats the permanent DV360 Floodlight slot (the "n" in u-n, see ASB-3533) that
// cohorts.py's assignDV360Slots() reports back on activation, into a toast suffix.
// Shared by the two places an audience gets activated to DV360 from the UI: the
// Audience Manager's bulk "Push to Destinations" action and the Audience Builder's
// own Destinations save flow. See ASB-3580.

// Matches backend's DV360_ACTIVATION_METHOD_ID (cohorts.py). Activation method ids are
// a stable backend contract; the display name ("DV360") is not — match on this, not on name/label.
export const DV360_ACTIVATION_METHOD_ID = 13;

/**
 * @param {Object|undefined} dv360Slots - unique_id -> assigned slot, from
 *   PUT /cohorts/sync's response. Missing/empty when no cohort in this request
 *   ended up DV360-active with a known slot.
 * @param {Array<{value: string, label: string}>} cohorts - the cohorts included in
 *   the request, used to resolve each unique_id back to a display name.
 * @returns {string} e.g. " — DV360 slot assigned: Sports: u4" or "" when nothing to show.
 */
export function formatDv360SlotsSuffix(dv360Slots, cohorts) {
	const slots = dv360Slots ?? {};
	const lines = (cohorts ?? [])
		.filter((c) => slots[c.value] != null)
		.map((c) => `${c.label}: u${slots[c.value]}`);
	if (!lines.length) return "";
	return ` — DV360 slot${lines.length > 1 ? "s" : ""} assigned: ${lines.join(", ")}`;
}

/**
 * @param {number|null|undefined} dv360Slot - from PUT /cohorts/<uid>'s response.
 *   Null/undefined when the update didn't leave the cohort DV360-active with a
 *   known slot.
 * @returns {string} e.g. " — DV360 slot: u4" or "" when nothing to show.
 */
export function formatDv360SlotSuffix(dv360Slot) {
	return dv360Slot != null ? ` — DV360 slot: u${dv360Slot}` : "";
}

/**
 * Names which audiences hit DV360 slot exhaustion (ASB-3549), for a bulk push where the
 * write itself still succeeded — mirrors formatDv360SlotsSuffix's shape and, like it, an
 * empty result means nothing to show (skip the toast branch that would call this).
 * @param {Object|undefined} dv360SlotErrors - unique_id -> human-readable error, from
 *   PUT /cohorts/sync's response.
 * @param {Array<{value: string, label: string}>} cohorts - the cohorts included in
 *   the request, used to resolve each unique_id back to a display name.
 * @returns {string} e.g. " — Sports, News: DV360 slot space exhausted..." or "".
 */
export function formatDv360SlotErrorsSuffix(dv360SlotErrors, cohorts) {
	const errors = dv360SlotErrors ?? {};
	const ids = Object.keys(errors);
	if (!ids.length) return "";
	const names = (cohorts ?? []).filter((c) => errors[c.value] != null).map((c) => c.label);
	const label = names.length
		? names.join(", ")
		: `${ids.length} audience${ids.length > 1 ? "s" : ""}`;
	// Every entry shares the same silo-wide exhaustion cause today, so one representative
	// message (not one repeated per audience) keeps the toast readable.
	return ` — ${label}: ${Object.values(errors)[0]}`;
}
